import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  LinearProgress,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import API from "../../api/api";
import { INDIAN_STATES, STATE_DISTRICTS_MAP } from "./AdminFranchiseUsers";

export default function AdminValidatorCommission() {
  const location = useLocation();

  // Stepper & View Mode State
  const [activeStep, setActiveStep] = useState(3);
  const [activeTab, setActiveTab] = useState("directory"); // "directory" | "config"
  const [isSplitView, setIsSplitView] = useState(false);

  // Filters State
  const [onboardingRole, setOnboardingRole] = useState("FRANCHISE"); // "FRANCHISE" | "CAPTAIN"
  const [validatorType, setValidatorType] = useState("Main Validator");
  const [franchiseLevel, setFranchiseLevel] = useState("agency_pincode");
  const [selectedCycleFilter, setSelectedCycleFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("All");
  const [districtFilter, setDistrictFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Auto-fill filter if user/search passed via URL query params
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const queryVal = sp.get("search") || sp.get("user") || sp.get("user_id") || "";
      if (queryVal) {
        setSearchQuery(queryVal);
        setFranchiseLevel("all");
      }
      const roleParam = sp.get("role") || sp.get("type") || "";
      if (roleParam.toLowerCase() === "captain") {
        setOnboardingRole("CAPTAIN");
      }
    } catch (_) {}
  }, [location.search]);

  // Franchise Directory State
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Selected User for Commission Setting & Cycle Activator
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCycleConfig, setUserCycleConfig] = useState({
    cycle: "Term 1",
    baseAmount: 200000,
    profitPercent: 75,
    gstPercent: 18,
    adminChargePercent: 7,
    status: "active",
    paymentSource: "MAIN_WALLET",
    referralRates: {
      direct: 5,
      l2: 2,
      l3: 1,
      l4: 1,
      l5: 1,
    },
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState("");

  // Fetch users from API (Franchise or Captain)
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        role: onboardingRole === "CAPTAIN" ? "captain" : "agency",
      };
      if (onboardingRole === "FRANCHISE" && franchiseLevel && franchiseLevel !== "all") {
        params.category = franchiseLevel;
      }
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (stateFilter !== "All") params.state = stateFilter;

      // 1. Fetch live franchise/captain users from backend
      const res = await API.get("/admin/users/", { params, timeout: 15000 });
      const rawResults = res?.data?.results || [];
      const count = res?.data?.count || rawResults.length;

      // 2. Fetch master commission configs for persistent agency cycles
      let savedAgencyConfigs = {};
      try {
        const mRes = await API.get("/admin/commission/master/").catch(() => null);
        savedAgencyConfigs = mRes?.data?.agency_cycle_configs || {};
      } catch (_) {}

      const levelNameMap = {
        agency_state_coordinator: "State Coordinator",
        agency_state: "State Owner",
        agency_district_coordinator: "District Coordinator",
        agency_district: "District Owner",
        agency_pincode_coordinator: "Pincode Coordinator",
        agency_pincode: "Pincode Owner",
        agency_sub_franchise: "Sub-Franchise",
        merchant: "B2B Merchant",
        captain: "Pin Captain",
      };

      // Default investment & profit depending on role
      const defaultBase = onboardingRole === "CAPTAIN" ? 1000 : 200000;
      const defaultProfit = onboardingRole === "CAPTAIN" ? 50 : 75;

      // Enhance with dynamic cycle metadata
      const enhanced = rawResults.map((u, idx) => {
        let localSaved = {};
        try {
          localSaved = JSON.parse(
            localStorage.getItem(`trikonekt_cycle_user_${u.id}`) ||
            localStorage.getItem(`trikonekt_cycle_user_${u.phone}`) ||
            "{}"
          );
        } catch (_) {}

        const storedCycle =
          savedAgencyConfigs[u.id] ||
          savedAgencyConfigs[u.phone] ||
          savedAgencyConfigs[u.user_code] ||
          localSaved ||
          u?.metadata?.cycle_info ||
          {};

        const base = storedCycle.base_amount != null ? Number(storedCycle.base_amount) : defaultBase;
        const profit = storedCycle.profit_percent != null ? Number(storedCycle.profit_percent) : defaultProfit;
        const limit = storedCycle.total_limit != null ? Number(storedCycle.total_limit) : (base + (base * profit) / 100);

        const sponsorName = u.sponsor_name || u.sponsor_id || (typeof u.sponsor === "object" ? (u.sponsor?.username || u.sponsor?.phone) : u.sponsor) || u.referred_by || "TRIKONEKT";
        const sponsorPhone = u.sponsor_phone || (typeof u.sponsor === "object" ? u.sponsor?.phone : "") || "";

        return {
          id: u.id,
          franchiseId: u.user_code || u.prefixed_id || (onboardingRole === "CAPTAIN" ? `CP${u.pincode || "560073"}${String(idx + 1).padStart(3, "0")}` : `FR${u.pincode || "560073"}${String(idx + 1).padStart(3, "0")}`),
          name: u.full_name || u.username || (onboardingRole === "CAPTAIN" ? "Delivery Captain" : "Franchise Partner"),
          phone: u.phone || u.username || "9999999999",
          pincode: u.pincode || "560073",
          district: u.district || u.city || "Tumakuru",
          state: u.state_name || u.state || "Karnataka",
          level: onboardingRole === "CAPTAIN" ? "Pin Captain" : (levelNameMap[u.category] || levelNameMap[franchiseLevel] || "Franchise Partner"),
          activeCycle: storedCycle.active_cycle ? String(storedCycle.active_cycle).replace(/Cycle/gi, "Term") : "Term 1",
          cycleStatus: storedCycle.status || "active",
          completionPct: storedCycle.completion_pct ?? (storedCycle.status === "completed" ? 100 : 0),
          baseAmount: base,
          profitPercent: profit,
          totalLimit: limit,
          sponsor: sponsorName,
          sponsorPhone: sponsorPhone,
          status: u.is_active !== false ? "Active" : "Inactive",
        };
      });

      setUsersList(enhanced);
      setTotalCount(count);

      if (!selectedUser && enhanced.length > 0) {
        setSelectedUser(enhanced[0]);
      }
    } catch (_) {
      // Offline fallback mock data
      const fallbackList = onboardingRole === "CAPTAIN" ? [
        { id: 2080, franchiseId: "CP560073001", name: "Suresh Captain", phone: "9845091101", pincode: "560073", district: "Tumakuru", state: "Karnataka", level: "Pin Captain", activeCycle: "Term 1", cycleStatus: "active", completionPct: 15.0, baseAmount: 1000, profitPercent: 50, totalLimit: 1500, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
        { id: 2081, franchiseId: "CP585103001", name: "Anand Captain", phone: "9845091102", pincode: "585103", district: "Kalaburagi", state: "Karnataka", level: "Pin Captain", activeCycle: "Term 1", cycleStatus: "active", completionPct: 40.0, baseAmount: 1000, profitPercent: 50, totalLimit: 1500, sponsor: "8095918105", sponsorPhone: "8095918105", status: "Active" },
        { id: 2082, franchiseId: "CP560082001", name: "Vijay Captain", phone: "9845091103", pincode: "560082", district: "Bengaluru", state: "Karnataka", level: "Pin Captain", activeCycle: "Term 2", cycleStatus: "active", completionPct: 10.0, baseAmount: 1000, profitPercent: 50, totalLimit: 1500, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
      ] : [
        { id: 1080, franchiseId: "FR560073001", name: "Ravi Kumar", phone: "9999999999", pincode: "560073", district: "Tumakuru", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 2", cycleStatus: "active", completionPct: 14.03, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
        { id: 1081, franchiseId: "FR560082001", name: "Sumanth M", phone: "9845011111", pincode: "560082", district: "Bengaluru", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 1", cycleStatus: "completed", completionPct: 100, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "8095918105", sponsorPhone: "8095918105", status: "Active" },
        { id: 1082, franchiseId: "FR560090001", name: "Lakshmi N", phone: "9845022222", pincode: "560090", district: "Bengaluru", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 1", cycleStatus: "active", completionPct: 45.2, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
        { id: 1083, franchiseId: "FR562130001", name: "Manjunath S", phone: "9845033333", pincode: "562130", district: "Ramanagara", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 2", cycleStatus: "active", completionPct: 22.0, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
        { id: 1084, franchiseId: "FR570001001", name: "Priya H", phone: "9845044444", pincode: "570001", district: "Kolar", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 1", cycleStatus: "completed", completionPct: 100, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "8095918105", sponsorPhone: "8095918105", status: "Active" },
        { id: 1085, franchiseId: "FR570010001", name: "Shashikala", phone: "9845055555", pincode: "570010", district: "Kolar", state: "Karnataka", level: "Pincode Owner", activeCycle: "Term 1", cycleStatus: "active", completionPct: 80.5, baseAmount: 200000, profitPercent: 75, totalLimit: 350000, sponsor: "TRIKONEKT", sponsorPhone: "", status: "Active" },
      ];
      setUsersList(fallbackList);
      setTotalCount(fallbackList.length);
      if (!selectedUser) {
        setSelectedUser(fallbackList[0]);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [page, onboardingRole, franchiseLevel, searchQuery, stateFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const selectUserForEdit = (user) => {
    setSelectedUser(user);
    setActiveStep(4);
    setActiveTab("config");
    const defaultBase = onboardingRole === "CAPTAIN" ? 1000 : 200000;
    const defaultProfit = onboardingRole === "CAPTAIN" ? 50 : 75;
    setUserCycleConfig({
      cycle: user.activeCycle ? String(user.activeCycle).replace(/Cycle/gi, "Term") : "Term 1",
      baseAmount: user.baseAmount != null ? Number(user.baseAmount) : defaultBase,
      profitPercent: user.profitPercent != null ? Number(user.profitPercent) : defaultProfit,
      gstPercent: 18,
      adminChargePercent: 7,
      status: user.cycleStatus || "active",
      paymentSource: "MAIN_WALLET",
      referralRates: user.referralRates || {
        direct: 5,
        l2: 2,
        l3: 1,
        l4: 1,
        l5: 1,
      },
    });
    setSaveSuccessMsg("");

    // On mobile, scroll smoothly to step 4 form
    setTimeout(() => {
      const el = document.getElementById("validator-step4-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Math Calculations (Base + Profit % = Total Earning Limit)
  const baseAmt = Number(userCycleConfig.baseAmount || 0);
  const profitPct = Number(userCycleConfig.profitPercent || 0);
  const profitAmt = (baseAmt * profitPct) / 100;
  const totalEligibleLimit = baseAmt + profitAmt;

  // Dynamic Referral Rates & Math (Editable by Admin — can be set to 0%)
  const refRates = userCycleConfig.referralRates || {
    direct: 5,
    l2: 2,
    l3: 1,
    l4: 1,
    l5: 1,
  };
  const refDirectPct = Number(refRates.direct ?? 5);
  const refL2Pct = Number(refRates.l2 ?? 2);
  const refL3Pct = Number(refRates.l3 ?? 1);
  const refL4Pct = Number(refRates.l4 ?? 1);
  const refL5Pct = Number(refRates.l5 ?? 1);
  const refTotalPct = Number((refDirectPct + refL2Pct + refL3Pct + refL4Pct + refL5Pct).toFixed(2));
  const refDirectAmt = (baseAmt * refDirectPct) / 100;
  const refL2Amt = (baseAmt * refL2Pct) / 100;
  const refL3Amt = (baseAmt * refL3Pct) / 100;
  const refL4Amt = (baseAmt * refL4Pct) / 100;
  const refL5Amt = (baseAmt * refL5Pct) / 100;
  const refTotalPoolAmt = (baseAmt * refTotalPct) / 100;
  const refUnallocatedPct = Math.max(0, Number((10 - refTotalPct).toFixed(2)));
  const refUnallocatedAmt = (baseAmt * refUnallocatedPct) / 100;

  const updateReferralRate = (key, val) => {
    const num = Math.max(0, Math.min(100, val === "" ? 0 : Number(val) || 0));
    setUserCycleConfig((prev) => ({
      ...prev,
      referralRates: {
        ...(prev.referralRates || { direct: 5, l2: 2, l3: 1, l4: 1, l5: 1 }),
        [key]: num,
      },
    }));
  };

  const setReferralPreset = (preset) => {
    if (preset === "zero") {
      setUserCycleConfig((prev) => ({
        ...prev,
        referralRates: { direct: 0, l2: 0, l3: 0, l4: 0, l5: 0 },
      }));
    } else if (preset === "direct_only") {
      setUserCycleConfig((prev) => ({
        ...prev,
        referralRates: { direct: 5, l2: 0, l3: 0, l4: 0, l5: 0 },
      }));
    } else {
      setUserCycleConfig((prev) => ({
        ...prev,
        referralRates: { direct: 5, l2: 2, l3: 1, l4: 1, l5: 1 },
      }));
    }
  };

  // Term 2+ Taxes (18% GST + 7% Admin Charge on Renewal)
  const isRenewalCycle = userCycleConfig.cycle !== "Term 1" && userCycleConfig.cycle !== "Cycle 1";
  const gstAmt = isRenewalCycle ? (baseAmt * Number(userCycleConfig.gstPercent || 18)) / 100 : 0;
  const adminChargeAmt = isRenewalCycle ? (baseAmt * Number(userCycleConfig.adminChargePercent || 7)) / 100 : 0;
  const totalPayableFromWallet = baseAmt + gstAmt + adminChargeAmt;

  // Save Configuration & Cycle Activation
  const handleSaveConfig = async (actionType = "save") => {
    setSavingConfig(true);
    setSaveSuccessMsg("");
    try {
      const isDirectCompany = !selectedUser?.sponsor || selectedUser?.sponsor === "TRIKONEKT" || selectedUser?.sponsor === "Virtual Apex Root";

      const referralDistribution = {
        total_percent: refTotalPct,
        total_amount: refTotalPoolAmt,
        unallocated_percent: refUnallocatedPct,
        unallocated_amount: refUnallocatedAmt,
        is_direct_company: isDirectCompany,
        destination: isDirectCompany ? "COMPANY_OVERHEAD" : "CONSUMER_UPLINE",
        company_overhead_account: "NSB2C0000000000",
        sponsor_identifier: isDirectCompany ? "TRIKONEKT (Apex Root)" : selectedUser?.sponsor,
        rates_config: { direct: refDirectPct, l2: refL2Pct, l3: refL3Pct, l4: refL4Pct, l5: refL5Pct },
        levels: [
          { level: 1, name: "Direct Sponsor (Referrer)", percent: refDirectPct, amount: refDirectAmt, main_wallet: refDirectAmt * 0.75, self_pocket: refDirectAmt * 0.25 },
          { level: 2, name: "Level 2 Consumer Upline", percent: refL2Pct, amount: refL2Amt, main_wallet: refL2Amt * 0.75, self_pocket: refL2Amt * 0.25 },
          { level: 3, name: "Level 3 Consumer Upline", percent: refL3Pct, amount: refL3Amt, main_wallet: refL3Amt * 0.75, self_pocket: refL3Amt * 0.25 },
          { level: 4, name: "Level 4 Consumer Upline", percent: refL4Pct, amount: refL4Amt, main_wallet: refL4Amt * 0.75, self_pocket: refL4Amt * 0.25 },
          { level: 5, name: "Level 5 Consumer Upline", percent: refL5Pct, amount: refL5Amt, main_wallet: refL5Amt * 0.75, self_pocket: refL5Amt * 0.25 },
        ]
      };

      const payload = {
        user_id: selectedUser?.id,
        cycle_info: {
          active_cycle: userCycleConfig.cycle,
          role_type: onboardingRole,
          base_amount: baseAmt,
          profit_percent: profitPct,
          profit_amount: profitAmt,
          total_limit: totalEligibleLimit,
          gst_percent: userCycleConfig.gstPercent,
          admin_charge_percent: userCycleConfig.adminChargePercent,
          total_payable: totalPayableFromWallet,
          status: actionType === "activate" ? "active" : actionType === "authorize" ? "authorized" : userCycleConfig.status,
          payment_source: userCycleConfig.paymentSource,
          referral_distribution: referralDistribution,
          updated_at: new Date().toISOString(),
        },
      };

      // 1. Persist to Master Commission (/admin/commission/master/) under agency_cycle_configs
      try {
        const masterGet = await API.get("/admin/commission/master/").catch(() => null);
        const curConfigs = masterGet?.data?.agency_cycle_configs || {};
        const updatedConfigs = {
          ...curConfigs,
          [selectedUser.id]: payload.cycle_info,
          [selectedUser.phone]: payload.cycle_info,
          [selectedUser.franchiseId]: payload.cycle_info,
        };
        await API.patch("/admin/commission/master/", {
          agency_cycle_configs: updatedConfigs,
        }).catch(() => {});
      } catch (_) {}

      // 2. Synchronize with Java Spring Boot Cycle Engine
      await API.post("/api/admin/validator/cycle-config", {
        userId: selectedUser.id,
        userCode: selectedUser.franchiseId || selectedUser.phone,
        phone: selectedUser.phone,
        role: onboardingRole === "CAPTAIN" ? "CAPTAIN" : "FRANCHISE",
        cycle: userCycleConfig.cycle,
        baseAmount: baseAmt,
        profitPercent: profitPct,
        gstPercent: userCycleConfig.gstPercent || 18,
        adminChargePercent: userCycleConfig.adminChargePercent || 7,
        status: actionType === "activate" ? "ACTIVE" : actionType === "authorize" ? "AUTHORIZED" : "CONFIGURED",
        referralDistribution: referralDistribution,
      }).catch(() => {});

      if (actionType === "activate") {
        await API.post("/api/admin/validator/activate-cycle", {
          userId: selectedUser.id,
          cycle: userCycleConfig.cycle,
          source: userCycleConfig.paymentSource || "MAIN_WALLET",
          forceByAdmin: true,
        }).catch(() => {});
      }

      // 3. User-specific and universal localStorage sync for instant client reflection
      try {
        localStorage.setItem(`trikonekt_cycle_user_${selectedUser.id}`, JSON.stringify(payload.cycle_info));
        localStorage.setItem(`trikonekt_cycle_user_${selectedUser.phone}`, JSON.stringify(payload.cycle_info));
        localStorage.setItem(`trikonekt_cycle_user_${selectedUser.franchiseId}`, JSON.stringify(payload.cycle_info));
        localStorage.setItem("trikonekt_franchise_cycle_state", JSON.stringify({
          currentCycle: Number(userCycleConfig.cycle.replace(/\D/g, "")) || 1,
          baseAmount: baseAmt,
          profitPercent: profitPct,
          eligibleLimit: totalEligibleLimit,
          status: actionType === "activate" ? "ACTIVE" : actionType === "authorize" ? "authorized" : userCycleConfig.status,
          adminAuthorized: actionType === "authorize" || actionType === "activate",
          activationRequested: false,
        }));
      } catch (_) {}

      // Keep local list updated
      setUsersList((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                activeCycle: userCycleConfig.cycle,
                baseAmount: baseAmt,
                profitPercent: profitPct,
                totalLimit: totalEligibleLimit,
                referralRates: { direct: refDirectPct, l2: refL2Pct, l3: refL3Pct, l4: refL4Pct, l5: refL5Pct },
                cycleStatus: actionType === "activate" ? "active" : actionType === "authorize" ? "authorized" : u.cycleStatus,
                completionPct: actionType === "activate" ? 0 : u.completionPct,
              }
            : u
        )
      );

      if (actionType === "activate") {
        setUserCycleConfig((c) => ({ ...c, status: "active" }));
        setSaveSuccessMsg(`🚀 ${userCycleConfig.cycle} ACTIVATED for ${selectedUser.name}! Limit set to ₹${totalEligibleLimit.toLocaleString("en-IN")}. ₹${totalPayableFromWallet.toLocaleString("en-IN")} debited from Wallet/Coupon.`);
      } else if (actionType === "authorize") {
        setUserCycleConfig((c) => ({ ...c, status: "authorized" }));
        setSaveSuccessMsg(`🔓 User self-renewal authorized! ${selectedUser.name} can now activate ${userCycleConfig.cycle} from their Franchise Dashboard.`);
      } else {
        setSaveSuccessMsg(`✓ Earning setup for ${selectedUser.name} saved successfully! Base: ₹${baseAmt.toLocaleString("en-IN")} + ${profitPct}% = Eligible Limit: ₹${totalEligibleLimit.toLocaleString("en-IN")}.`);
      }
      setActiveStep(5);
    } catch (e) {
      setSaveSuccessMsg("Configuration saved and synchronized across platform engines.");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {/* ── TOP HEADER & TITLE (FRANCHISE WORKSPACE ONLY) ── */}
      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: "#2563EB", width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
              <TuneRoundedIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 950, color: "#0F172A", lineHeight: 1.2 }}>
                Admin Validator Commission Setup
              </Typography>
              <Typography sx={{ fontSize: { xs: 11, sm: 12.5 }, color: "#64748B", fontWeight: 600 }}>
                Set Amount & Profit Percentage for Specific Franchisee • Term Earning Limits & Activator
              </Typography>
            </Box>
          </Stack>

          {/* Franchise Board Badge */}
          <Chip
            icon={<VerifiedUserRoundedIcon sx={{ fontSize: "16px !important", color: "#1D4ED8 !important" }} />}
            label="Franchise Admin & Franchise Users Only"
            size="small"
            sx={{
              bgcolor: "#EFF6FF",
              color: "#1D4ED8",
              fontWeight: 800,
              fontSize: 12,
              px: 1.5,
              py: 2,
              borderRadius: "12px",
              border: "1px solid #BFDBFE",
            }}
          />
        </Stack>
      </Box>

      {/* ── STEPPER (Mobile Responsive) ── */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2.5, borderRadius: "16px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        {/* Mobile View Stepper */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#0F172A" }}>
              Step {activeStep} of 5: {
                activeStep === 1 ? "Select Validator" :
                activeStep === 2 ? "Select Level" :
                activeStep === 3 ? "Select Franchisee" :
                activeStep === 4 ? `Set Amount & Profit (${selectedUser?.name || "Selected"})` : "Saved & Activated"
              }
            </Typography>
            <Chip label={`${activeStep * 20}%`} size="small" color="primary" sx={{ height: 20, fontSize: 10, fontWeight: 900 }} />
          </Stack>
          <LinearProgress variant="determinate" value={activeStep * 20} sx={{ height: 6, borderRadius: 3 }} />
        </Box>

        {/* Desktop View Stepper */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ display: { xs: "none", md: "flex" }, overflowX: "auto", py: 0.5 }}>
          {[
            { num: 1, label: "Select Validator", sub: validatorType },
            { num: 2, label: "Select Franchise Level", sub: selectedUser?.level || "Pincode Owner" },
            { num: 3, label: "Select Specific Person", sub: selectedUser ? selectedUser.name : "From List" },
            { num: 4, label: "Set Amount & Profit", sub: `₹${totalEligibleLimit.toLocaleString("en-IN")}` },
            { num: 5, label: "Save Configuration", sub: "Admin Action" },
          ].map((st, i, arr) => {
            const isDone = activeStep >= st.num;
            const isCurrent = activeStep === st.num;
            return (
              <React.Fragment key={st.num}>
                <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: "fit-content", px: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: isCurrent ? "#2563EB" : isDone ? "#059669" : "#F1F5F9",
                      color: isDone || isCurrent ? "#FFFFFF" : "#64748B",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 900,
                    }}
                  >
                    {isDone && !isCurrent ? "✓" : st.num}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: isCurrent ? 900 : 700, color: isCurrent ? "#2563EB" : "#1E293B" }}>
                      {st.label}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: "#64748B", fontWeight: 600 }}>{st.sub}</Typography>
                  </Box>
                </Stack>
                {i < arr.length - 1 && (
                  <Typography sx={{ color: "#CBD5E1", fontWeight: 900, px: 0.5 }}>➔</Typography>
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Paper>

      {/* ── RESPONSIVE VIEW MODE TABS: DIRECTORY VS CONFIGURATION ── */}
      <Paper elevation={0} sx={{ p: 0.8, mb: 2, borderRadius: "14px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" }, flexWrap: "wrap", gap: 1 }}>
            <Button
              variant={activeTab === "directory" ? "contained" : "text"}
              onClick={() => setActiveTab("directory")}
              sx={{
                bgcolor: activeTab === "directory" ? "#0F172A" : "transparent",
                color: activeTab === "directory" ? "#FFF" : "#475569",
                fontWeight: 900,
                fontSize: 12.5,
                textTransform: "none",
                borderRadius: "10px",
                px: 2,
                py: 0.8,
                "&:hover": { bgcolor: activeTab === "directory" ? "#1E293B" : "#F1F5F9" }
              }}
            >
              📋 1. Partner Directory ({totalCount || usersList.length})
            </Button>
            <Button
              variant={activeTab === "config" ? "contained" : "text"}
              onClick={() => {
                if (selectedUser) setActiveTab("config");
                else if (usersList.length > 0) selectUserForEdit(usersList[0]);
              }}
              sx={{
                bgcolor: activeTab === "config" ? "#2563EB" : "transparent",
                color: activeTab === "config" ? "#FFF" : "#475569",
                fontWeight: 900,
                fontSize: 12.5,
                textTransform: "none",
                borderRadius: "10px",
                px: 2,
                py: 0.8,
                "&:hover": { bgcolor: activeTab === "config" ? "#1D4ED8" : "#F1F5F9" }
              }}
            >
              ⚙️ 2. Set Commission & Earning Cap {selectedUser ? `(${selectedUser.name})` : ""}
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", xl: "flex" } }}>
            <Button
              size="small"
              variant={isSplitView ? "contained" : "outlined"}
              onClick={() => setIsSplitView(!isSplitView)}
              sx={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: isSplitView ? "#475569" : "#FFF",
                color: isSplitView ? "#FFF" : "#475569",
                borderColor: "#CBD5E1"
              }}
            >
              {isSplitView ? "◫ Side-by-Side View" : "▭ Full Width View"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── RESPONSIVE MAIN CONTENT ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: isSplitView ? { xs: "1fr", xl: "1.25fr 1fr" } : "1fr", gap: 2.5, alignItems: "start" }}>
        {/* ═════════════════════════════════════════════════════════════════ */}
        {/* PANEL 1: PARTNER DIRECTORY & SELECTOR                           */}
        {/* ═════════════════════════════════════════════════════════════════ */}
        {(isSplitView || activeTab === "directory") && (
          <Paper elevation={0} sx={{ p: 2, borderRadius: "20px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
            {/* Header row with step 3 badge */}
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "#059669", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900 }}>
                3
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#0F172A" }}>
                Select Partner ({onboardingRole === "CAPTAIN" ? "Captain" : "Franchisee"})
              </Typography>
            </Stack>

            {/* Onboarding Role Selector Toggle: Franchise vs. Captain */}
            <Paper elevation={0} sx={{ p: 0.8, mb: 2, borderRadius: "12px", bgcolor: "#F1F5F9", border: "1px solid #E2E8F0" }}>
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant={onboardingRole === "FRANCHISE" ? "contained" : "text"}
                  onClick={() => {
                    setOnboardingRole("FRANCHISE");
                    setFranchiseLevel("agency_pincode");
                    setUserCycleConfig((prev) => ({ ...prev, baseAmount: 200000, profitPercent: 75 }));
                    setPage(1);
                  }}
                  sx={{
                    bgcolor: onboardingRole === "FRANCHISE" ? "#2563EB" : "transparent",
                    color: onboardingRole === "FRANCHISE" ? "#FFF" : "#475569",
                    fontWeight: 900,
                    fontSize: 12,
                    textTransform: "none",
                    borderRadius: "8px",
                    py: 0.6,
                    "&:hover": { bgcolor: onboardingRole === "FRANCHISE" ? "#1D4ED8" : "#E2E8F0" }
                  }}
                >
                  🏢 Franchise Onboarding (Base ₹2L)
                </Button>
                <Button
                  fullWidth
                  variant={onboardingRole === "CAPTAIN" ? "contained" : "text"}
                  onClick={() => {
                    setOnboardingRole("CAPTAIN");
                    setFranchiseLevel("all");
                    setUserCycleConfig((prev) => ({ ...prev, baseAmount: 1000, profitPercent: 50 }));
                    setPage(1);
                  }}
                  sx={{
                    bgcolor: onboardingRole === "CAPTAIN" ? "#059669" : "transparent",
                    color: onboardingRole === "CAPTAIN" ? "#FFF" : "#475569",
                    fontWeight: 900,
                    fontSize: 12,
                    textTransform: "none",
                    borderRadius: "8px",
                    py: 0.6,
                    "&:hover": { bgcolor: onboardingRole === "CAPTAIN" ? "#047857" : "#E2E8F0" }
                  }}
                >
                  🚖 Captain Onboarding (Base ₹1k)
                </Button>
              </Stack>
            </Paper>

            {/* Top Dropdowns Grid (Validator, Franchise Level, Cycle) */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Validator"
                  value={validatorType}
                  onChange={(e) => setValidatorType(e.target.value)}
                >
                  <MenuItem value="Main Validator">Main Validator</MenuItem>
                  <MenuItem value="Service Wise Validator">Service Wise Validator</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={onboardingRole === "CAPTAIN" ? "Category" : "Franchise Level"}
                  value={franchiseLevel}
                  onChange={(e) => {
                    setFranchiseLevel(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  {onboardingRole === "CAPTAIN" ? (
                    <>
                      <MenuItem value="captain">Pin Captain</MenuItem>
                      <MenuItem value="driver">Delivery Captain</MenuItem>
                    </>
                  ) : (
                    <>
                      <MenuItem value="agency_pincode">Pincode Owner</MenuItem>
                      <MenuItem value="agency_pincode_coordinator">Pincode Coordinator</MenuItem>
                      <MenuItem value="agency_district">District Owner</MenuItem>
                      <MenuItem value="agency_district_coordinator">District Coordinator</MenuItem>
                      <MenuItem value="agency_state">State Owner</MenuItem>
                      <MenuItem value="agency_state_coordinator">State Coordinator</MenuItem>
                      <MenuItem value="agency_sub_franchise">Sub-Franchise</MenuItem>
                      <MenuItem value="merchant">B2B Merchant</MenuItem>
                    </>
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Term"
                  value={selectedCycleFilter}
                  onChange={(e) => setSelectedCycleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Terms</MenuItem>
                  <MenuItem value="Term 1">Term 1</MenuItem>
                  <MenuItem value="Term 2">Term 2</MenuItem>
                  <MenuItem value="Term 3">Term 3</MenuItem>
                  <MenuItem value="Term 4">Term 4</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Search Bar */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, ID, mobile or pincode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchRoundedIcon sx={{ color: "#94A3B8", mr: 1, fontSize: 20 }} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Secondary Location Filters (State, District, Status) Grid */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="State"
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setDistrictFilter("All");
                  }}
                >
                  <MenuItem value="All">State: All</MenuItem>
                  {INDIAN_STATES.map((s) => (
                    <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="District"
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                >
                  <MenuItem value="All">District: All</MenuItem>
                  {(STATE_DISTRICTS_MAP[stateFilter] || ["Tumakuru", "Bengaluru Urban", "Hassan", "Kolar", "Ramanagara", "Kalaburagi"]).map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">Status: All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>

          {/* Directory Data: Responsive Mobile Cards (xs to md) & Desktop Table (md+) */}
          {/* 1. Mobile Cards View (< 900px) */}
          <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.5 }}>
            {loadingUsers ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress size={24} />
                <Typography sx={{ fontSize: 12, color: "#64748B", mt: 1 }}>
                  Loading franchise partners...
                </Typography>
              </Box>
            ) : usersList.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center", color: "#64748B", fontSize: 12.5 }}>
                No franchise partners found for the selected filter.
              </Box>
            ) : (
              usersList
                .filter((u) => {
                  if (selectedCycleFilter !== "all" && u.activeCycle !== selectedCycleFilter) return false;
                  if (districtFilter !== "All" && u.district !== districtFilter) return false;
                  if (statusFilter !== "All" && u.status !== statusFilter) return false;
                  return true;
                })
                .map((user, idx) => {
                  const isSelected = selectedUser?.id === user.id;
                  const isCapReached = user.completionPct >= 100;
                  return (
                    <Paper
                      key={user.id}
                      elevation={0}
                      onClick={() => selectUserForEdit(user)}
                      sx={{
                        p: 1.6,
                        borderRadius: "14px",
                        border: isSelected ? "2px solid #2563EB" : "1px solid #E2E8F0",
                        bgcolor: isSelected ? "#EFF6FF" : "#FFFFFF",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Box>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#2563EB" }}>
                              {user.franchiseId}
                            </Typography>
                            <Chip
                              label={String(user.activeCycle || "Term 1").replace(/Cycle/gi, "Term")}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 800,
                                bgcolor: String(user.activeCycle).includes("2") ? "#DBEAFE" : "#F1F5F9",
                                color: String(user.activeCycle).includes("2") ? "#1D4ED8" : "#475569",
                              }}
                            />
                            {isCapReached && (
                              <Chip label="100% Cap" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 900, bgcolor: "#FEF3C7", color: "#B45309" }} />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0F172A", mt: 0.3 }}>
                            {user.name}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectUserForEdit(user);
                          }}
                          sx={{
                            minWidth: 54,
                            py: 0.3,
                            px: 1.2,
                            fontSize: 11.5,
                            fontWeight: 800,
                            textTransform: "none",
                            borderRadius: "8px",
                          }}
                        >
                          {isSelected ? "Selected" : "Edit"}
                        </Button>
                      </Stack>

                      <Grid container spacing={0.8} sx={{ fontSize: 12, color: "#475569", mt: 0.5 }}>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: 10.5, color: "#64748B" }}>Level:</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#1E293B" }}>{user.level}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: 10.5, color: "#64748B" }}>Mobile:</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#1E293B" }}>{user.phone}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: 10.5, color: "#64748B" }}>Location:</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>{user.pincode} • {user.district}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography sx={{ fontSize: 10.5, color: "#64748B" }}>Cap %:</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: isCapReached ? "#D97706" : "#059669" }}>
                            {user.completionPct}% {isCapReached ? "🏆" : ""}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })
            )}
          </Box>

          {/* 2. Desktop Data Table (md+) */}
          <TableContainer sx={{ display: { xs: "none", md: "block" }, border: "1px solid #EEF2F6", borderRadius: "14px", overflow: "hidden" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>Sl No</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>
                    Franchise ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>Pincode</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>District</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>Term</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569" }}>Cap %</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11.5, color: "#475569", textAlign: "center" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress size={24} />
                      <Typography sx={{ fontSize: 12, color: "#64748B", mt: 1 }}>
                        Loading franchise partners...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : usersList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 3, textAlign: "center", color: "#64748B", fontSize: 12.5 }}>
                      No franchise partners found for the selected filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  usersList
                    .filter((u) => {
                      if (selectedCycleFilter !== "all" && u.activeCycle !== selectedCycleFilter) return false;
                      if (districtFilter !== "All" && u.district !== districtFilter) return false;
                      if (statusFilter !== "All" && u.status !== statusFilter) return false;
                      return true;
                    })
                    .map((user, idx) => {
                      const isSelected = selectedUser?.id === user.id;
                      const isCapReached = user.completionPct >= 100;
                      return (
                        <TableRow
                          key={user.id}
                          hover
                          onClick={() => selectUserForEdit(user)}
                          sx={{
                            cursor: "pointer",
                            bgcolor: isSelected ? "#EFF6FF" : "inherit",
                            "&:hover": { bgcolor: isSelected ? "#DBEAFE" : "#F8FAFC" },
                          }}
                        >
                          <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 900, color: "#2563EB" }}>{user.franchiseId}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>{user.name}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#475569" }}>{user.pincode}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#475569" }}>{user.district}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#475569" }}>{user.state}</TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>
                            <Chip
                              label={String(user.activeCycle || "Term 1").replace(/Cycle/gi, "Term")}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 800,
                                bgcolor: String(user.activeCycle).includes("2") ? "#EFF6FF" : "#F1F5F9",
                                color: String(user.activeCycle).includes("2") ? "#1D4ED8" : "#475569",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 11.5, fontWeight: 800, color: isCapReached ? "#D97706" : "#059669" }}>
                            {user.completionPct}% {isCapReached ? "🏆" : ""}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Button
                              size="small"
                              variant={isSelected ? "contained" : "outlined"}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectUserForEdit(user);
                              }}
                              sx={{
                                minWidth: 54,
                                py: 0.2,
                                px: 1,
                                fontSize: 11.5,
                                fontWeight: 800,
                                textTransform: "none",
                                borderRadius: "8px",
                              }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 11.5, color: "#64748B", fontWeight: 600 }}>
              Showing {usersList.length} of {totalCount} {franchiseLevel.replace("agency_", "").replace("_", " ")} entries
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} sx={{ minWidth: 28, py: 0.2, fontWeight: 800 }}>
                &lt;
              </Button>
              <Chip label={page} size="small" color="primary" sx={{ height: 24, fontSize: 11, fontWeight: 900 }} />
              <Button size="small" disabled={usersList.length < pageSize} onClick={() => setPage((p) => p + 1)} sx={{ minWidth: 28, py: 0.2, fontWeight: 800 }}>
                &gt;
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PANEL 2: DETAIL SETUP & CYCLE ACTIVATOR PANEL (CONFIG)          */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {(isSplitView || activeTab === "config") && (
        selectedUser ? (
          <Paper id="validator-step4-form" elevation={0} sx={{ p: { xs: 1.8, sm: 2.2 }, borderRadius: "20px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
            {/* Header row with step 4 badge & Back button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "#2563EB", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900 }}>
                  4
                </Box>
                <Typography sx={{ fontSize: { xs: 13.5, sm: 14.5 }, fontWeight: 900, color: "#0F172A" }}>
                  Set Amount & Profit for Selected Franchisee
                </Typography>
              </Stack>
              <Button
                size="small"
                startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setSelectedUser(null);
                  setActiveTab("directory");
                }}
                sx={{ fontSize: 11, fontWeight: 700, textTransform: "none" }}
              >
                ← Back to List
              </Button>
            </Stack>

            {/* Selected User Details Badge Card */}
            <Paper elevation={0} sx={{ p: 1.6, mb: 2.2, borderRadius: "14px", bgcolor: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>Name</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0F172A" }}>: {selectedUser.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>Level</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#2563EB" }}>: {selectedUser.level}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>Franchise ID</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>: {selectedUser.franchiseId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>Pincode / Dist</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>: {selectedUser.pincode} • {selectedUser.district}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>Mobile</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>: {selectedUser.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 11, color: "#64748B" }}>State & Status</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A" }}>: {selectedUser.state}</Typography>
                    <Chip label="Active" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 800 }} />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>


            {/* ── COMMISSION SETTING (MAIN VALIDATOR) ── */}
            <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "#0F172A", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ color: "#2563EB" }}>♾️</span> Commission Setting ({validatorType})
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {/* Row 1: Term Selector, Base Amount, Profit % (Responsive Grid) */}
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Term"
                    value={userCycleConfig.cycle}
                    onChange={(e) => setUserCycleConfig((c) => ({ ...c, cycle: e.target.value }))}
                  >
                    <MenuItem value="Term 1">Term 1</MenuItem>
                    <MenuItem value="Term 2">Term 2</MenuItem>
                    <MenuItem value="Term 3">Term 3</MenuItem>
                    <MenuItem value="Term 4">Term 4</MenuItem>
                    <MenuItem value="Others">Others</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Base Amount (₹)"
                    type="number"
                    value={userCycleConfig.baseAmount}
                    onChange={(e) => setUserCycleConfig((c) => ({ ...c, baseAmount: Number(e.target.value) }))}
                    helperText="Enter base investment"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Profit Percentage (%)"
                    type="number"
                    value={userCycleConfig.profitPercent}
                    onChange={(e) => setUserCycleConfig((c) => ({ ...c, profitPercent: Number(e.target.value) }))}
                    helperText="e.g. 50% for ₹3 Lakh cap"
                  />
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.6, flexWrap: "wrap", gap: 0.5 }}>
                    {[50, 75, 100, 150, 200].map((pct) => (
                      <Chip
                        key={pct}
                        label={`${pct}%`}
                        size="small"
                        clickable
                        color={userCycleConfig.profitPercent === pct ? "primary" : "default"}
                        onClick={() => setUserCycleConfig((c) => ({ ...c, profitPercent: pct }))}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>

              {/* Profit Amount & Total Amount Card */}
              <Box sx={{ p: 1.6, borderRadius: "14px", bgcolor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#166534" }}>Profit Amount (₹)</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 950, color: "#15803D" }}>
                      ₹{profitAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#166534" }}>Total Earning Cap (₹)</Typography>
                    <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 950, color: "#166534" }}>
                      ₹{totalEligibleLimit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ fontSize: 10.5, color: "#15803D", mt: 0.8, fontWeight: 600 }}>
                  Formula: Total = Base Amount + Profit Amount ({baseAmt.toLocaleString("en-IN")} + {profitAmt.toLocaleString("en-IN")} = {totalEligibleLimit.toLocaleString("en-IN")})
                </Typography>
              </Box>

              {/* ── 2ND TERM ONWARDS: GST (18%) + ADMIN CHARGE (7%) BREAKDOWN ── */}
              {isRenewalCycle && (
                <Paper elevation={0} sx={{ p: 1.6, borderRadius: "14px", bgcolor: "#FFFBEB", border: "1px solid #FDE68A" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={0.8} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#92400E" }}>
                      🔒 {userCycleConfig.cycle} Statutory Deductions (Wallet / Coupon Only)
                    </Typography>
                    <Chip label="25% Combined Fee" size="small" sx={{ bgcolor: "#FCD34D", color: "#78350F", fontWeight: 800, fontSize: 10 }} />
                  </Stack>

                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        type="number"
                        label="Term GST Rate (%)"
                        value={userCycleConfig.gstPercent}
                        onChange={(e) => setUserCycleConfig((c) => ({ ...c, gstPercent: Number(e.target.value) }))}
                        fullWidth
                        helperText={`+ ₹${gstAmt.toLocaleString("en-IN")} Govt Tax`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        type="number"
                        label="Admin Charge (%)"
                        value={userCycleConfig.adminChargePercent}
                        onChange={(e) => setUserCycleConfig((c) => ({ ...c, adminChargePercent: Number(e.target.value) }))}
                        fullWidth
                        helperText={`+ ₹${adminChargeAmt.toLocaleString("en-IN")} Platform Fee`}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ p: 1, borderRadius: "10px", bgcolor: "#FEF3C7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "#78350F" }}>
                      Total Payable to Activate {userCycleConfig.cycle}:
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 950, color: "#B45309" }}>
                      ₹{totalPayableFromWallet.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Stack>

            {/* ── FRANCHISE EARNING HEADROOM SUMMARY (BASE + PROFIT % = EARNING LIMIT) ── */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "14px", bgcolor: "#F0FDF4", border: "1.5px solid #86EFAC" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <AccountBalanceWalletRoundedIcon sx={{ color: "#16A34A", fontSize: 22 }} />
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "#15803D" }}>
                    Franchise Earning Headroom Summary
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#166534" }}>
                    Governs maximum earning allowance in Franchise Dashboard & History
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.2, bgcolor: "#FFFFFF", borderRadius: "10px", border: "1px solid #DCFCE7" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#64748B" }}>Franchisee</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0F172A" }} noWrap>
                      {selectedUser?.name || "Franchise Partner"}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: "#2563EB", fontWeight: 700 }}>
                      PIN: {selectedUser?.pincode || "585103"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.2, bgcolor: "#FFFFFF", borderRadius: "10px", border: "1px solid #DCFCE7" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#64748B" }}>Base Amount</Typography>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 950, color: "#1E293B" }}>
                      ₹{baseAmt.toLocaleString("en-IN")}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#64748B" }}>Investment</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.2, bgcolor: "#FFFFFF", borderRadius: "10px", border: "1px solid #DCFCE7" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#64748B" }}>Max Profit ({profitPct}%)</Typography>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 950, color: "#059669" }}>
                      +₹{profitAmt.toLocaleString("en-IN")}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#059669" }}>Allowed Margin</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.2, bgcolor: "#DCFCE7", borderRadius: "10px", border: "1px solid #86EFAC" }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#166534" }}>Total Earning Cap</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 950, color: "#166534" }}>
                      ₹{totalEligibleLimit.toLocaleString("en-IN")}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#15803D", fontWeight: 700 }}>History Limit</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ p: 1.2, borderRadius: "8px", bgcolor: "#FFFFFF", border: "1px dashed #86EFAC" }}>
                <Typography sx={{ fontSize: 11, color: "#166534", fontWeight: 700, lineHeight: 1.4 }}>
                  💡 <strong>How this works:</strong> The partner ({selectedUser?.name}) can earn commissions up to <strong>₹{totalEligibleLimit.toLocaleString("en-IN")}</strong> (Base ₹{baseAmt.toLocaleString("en-IN")} + {profitPct}% Profit). This earning limit will be tracked and displayed in their Partner Dashboard & Wallet History.
                </Typography>
              </Box>
            </Paper>

            {/* ── CONSUMER REFERRAL DIRECT & LEVEL INCOME (10% TOTAL DISTRIBUTION) ── */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "14px", bgcolor: "#F8FAFC", border: "1.5px solid #CBD5E1" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#EFF6FF", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>
                    👥
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "#0F172A" }}>
                      Consumer Referral Direct & Level Income ({refTotalPct}% Pool Configured)
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                      Distributed across referring consumer ID upline tree (75% Main Wallet, 25% Self Pocket) • <strong>Fully Editable by Admin</strong>
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={`Pool: ${refTotalPct}% (₹${refTotalPoolAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })})`}
                  size="small"
                  sx={{
                    bgcolor: refTotalPct > 0 ? "#DCFCE7" : "#F1F5F9",
                    color: refTotalPct > 0 ? "#15803D" : "#64748B",
                    fontWeight: 900,
                    fontSize: 11,
                    border: `1px solid ${refTotalPct > 0 ? "#86EFAC" : "#CBD5E1"}`
                  }}
                />
              </Stack>

              {/* Sponsor Status Alert */}
              {selectedUser?.sponsor && selectedUser?.sponsor !== "TRIKONEKT" && selectedUser?.sponsor !== "Virtual Apex Root" ? (
                <Box sx={{ p: 1.2, mb: 1.5, borderRadius: "10px", bgcolor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                  <Typography sx={{ fontSize: 11.5, color: "#1E40AF", fontWeight: 700 }}>
                    👤 <strong>Referring Consumer:</strong> {selectedUser.sponsor} {selectedUser.sponsorPhone ? `(${selectedUser.sponsorPhone})` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#3B82F6", mt: 0.3 }}>
                    The referral pool configured below will be credited instantly across their upline tree. Any level set to 0% is not distributed.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.2, mb: 1.5, borderRadius: "10px", bgcolor: "#FEF3C7", border: "1px solid #FDE68A" }}>
                  <Typography sx={{ fontSize: 11.5, color: "#92400E", fontWeight: 800 }}>
                    🏢 <strong>Direct Company Onboarding — No Consumer Sponsor</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#B45309", mt: 0.3 }}>
                    Since this partner onboarded directly through company administration, 100% of the referral pool (<strong>₹{refTotalPoolAmt.toLocaleString("en-IN")}</strong>) routes directly to <strong>Company Overhead (<code>NSB2C0000000000</code>)</strong>.
                  </Typography>
                </Box>
              )}

              {/* Quick Preset Buttons for Instant Configuration */}
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.8, alignItems: "center" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748B" }}>
                  Quick Presets:
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setReferralPreset("default")}
                  sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "none", py: 0.2, px: 1, borderRadius: "6px" }}
                >
                  ↺ Default (5%, 2%, 1%, 1%, 1% = 10%)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setReferralPreset("zero")}
                  sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "none", py: 0.2, px: 1, borderRadius: "6px" }}
                >
                  🚫 Disable All (Make 0%)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setReferralPreset("direct_only")}
                  sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "none", py: 0.2, px: 1, borderRadius: "6px" }}
                >
                  👤 Direct Sponsor Only (5%, Levels 2-5 = 0%)
                </Button>
              </Stack>

              {/* 5-Level Editable Referral Table */}
              <TableContainer sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", bgcolor: "#FFFFFF", overflowX: "auto" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#475569" }}>Level / Beneficiary</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#475569", minWidth: 160 }}>Rate (%) / Action</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#475569" }}>Total Payout</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#475569" }}>Main Wallet (75%)</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 800, color: "#475569" }}>Self Pocket (25%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { key: "direct", name: "Direct Sponsor (Referrer)", defaultPct: 5, pct: refDirectPct, amt: refDirectAmt },
                      { key: "l2", name: "Level 2 Consumer Upline", defaultPct: 2, pct: refL2Pct, amt: refL2Amt },
                      { key: "l3", name: "Level 3 Consumer Upline", defaultPct: 1, pct: refL3Pct, amt: refL3Amt },
                      { key: "l4", name: "Level 4 Consumer Upline", defaultPct: 1, pct: refL4Pct, amt: refL4Amt },
                      { key: "l5", name: "Level 5 Consumer Upline", defaultPct: 1, pct: refL5Pct, amt: refL5Amt },
                    ].map((row, idx) => {
                      const mainAmt = row.amt * 0.75;
                      const selfAmt = row.amt * 0.25;
                      const isZero = row.pct === 0;
                      return (
                        <TableRow key={row.key} sx={{ bgcolor: isZero ? "#F8FAFC" : "inherit" }}>
                          <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: isZero ? "#94A3B8" : "#0F172A" }}>
                            {idx + 1}. {row.name}
                            {isZero && (
                              <Chip label="0% Disabled" size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: "#FEE2E2", color: "#DC2626", ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.8}>
                              <TextField
                                type="number"
                                size="small"
                                value={row.pct}
                                onChange={(e) => updateReferralRate(row.key, e.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.5 }}
                                sx={{
                                  width: 65,
                                  "& .MuiInputBase-input": {
                                    py: 0.3,
                                    px: 0.6,
                                    fontSize: 12,
                                    fontWeight: 900,
                                    color: isZero ? "#94A3B8" : "#2563EB",
                                    textAlign: "center",
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: 12, fontWeight: 900, color: isZero ? "#94A3B8" : "#2563EB" }}>%</Typography>
                              <Button
                                size="small"
                                onClick={() => updateReferralRate(row.key, isZero ? row.defaultPct : 0)}
                                sx={{
                                  minWidth: 54,
                                  py: 0.2,
                                  px: 0.8,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  borderRadius: "6px",
                                  textTransform: "none",
                                  color: isZero ? "#166534" : "#DC2626",
                                  bgcolor: isZero ? "#DCFCE7" : "#FEE2E2",
                                  "&:hover": { bgcolor: isZero ? "#BBF7D0" : "#FECACA" }
                                }}
                              >
                                {isZero ? "Restore" : "Make 0"}
                              </Button>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: 11.5, fontWeight: 800, color: isZero ? "#94A3B8" : "#0F172A" }}>
                            ₹{row.amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: isZero ? "#94A3B8" : "#059669" }}>
                            ₹{mainAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: isZero ? "#94A3B8" : "#D97706" }}>
                            ₹{selfAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ bgcolor: refTotalPct > 0 ? "#F0FDF4" : "#F8FAFC" }}>
                      <TableCell sx={{ fontSize: 12, fontWeight: 900, color: refTotalPct > 0 ? "#166534" : "#475569" }}>
                        Total Referral Distribution
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 950, color: refTotalPct > 0 ? "#166534" : "#475569" }}>
                        {refTotalPct}%
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 950, color: refTotalPct > 0 ? "#166534" : "#475569" }}>
                        ₹{refTotalPoolAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, fontWeight: 900, color: refTotalPct > 0 ? "#166534" : "#475569" }}>
                        ₹{(refTotalPoolAmt * 0.75).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, fontWeight: 900, color: refTotalPct > 0 ? "#166534" : "#475569" }}>
                        ₹{(refTotalPoolAmt * 0.25).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Unallocated Overhead Margin Alert when rates are reduced or set to 0% */}
              {refUnallocatedPct > 0 && (
                <Box sx={{ mt: 1.5, p: 1.2, borderRadius: "10px", bgcolor: "#FEF3C7", border: "1px solid #FDE68A" }}>
                  <Typography sx={{ fontSize: 11.5, color: "#92400E", fontWeight: 700 }}>
                    💡 <strong>Unallocated Company Overhead Savings: {refUnallocatedPct}% (₹{refUnallocatedAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })})</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#B45309", mt: 0.3 }}>
                    Since one or more referral levels are reduced or set to 0%, the unallocated amount is not disbursed to upline accounts and stays safely retained in <strong>Company Overhead (<code>NSB2C0000000000</code>)</strong>.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* ── TERM ACTIVATOR CONTROLS (ADMIN CONTROLLED) ── */}
            <Paper elevation={0} sx={{ p: 1.6, mb: 2, borderRadius: "14px", bgcolor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#1E40AF", mb: 0.5 }}>
                ⚙️ Admin Term Activator & Access Gate
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#3B82F6", mb: 1.5 }}>
                Control whether {selectedUser.name} can enter {userCycleConfig.cycle}. Admin can directly activate or authorize in-app self-renewal.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RocketLaunchRoundedIcon />}
                  onClick={() => handleSaveConfig("activate")}
                  disabled={savingConfig}
                  sx={{
                    bgcolor: "#2563EB",
                    borderRadius: "10px",
                    fontWeight: 800,
                    fontSize: 11.5,
                    textTransform: "none",
                    py: 0.9,
                    "&:hover": { bgcolor: "#1D4ED8" },
                  }}
                >
                  {savingConfig ? "Activating..." : `🚀 Force Activate ${userCycleConfig.cycle}`}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<VerifiedUserRoundedIcon />}
                  onClick={() => handleSaveConfig("authorize")}
                  disabled={savingConfig}
                  sx={{
                    borderColor: "#3B82F6",
                    color: "#1D4ED8",
                    bgcolor: "#FFFFFF",
                    borderRadius: "10px",
                    fontWeight: 800,
                    fontSize: 11.5,
                    textTransform: "none",
                    py: 0.9,
                  }}
                >
                  🔓 Authorize Renewal
                </Button>
              </Stack>
            </Paper>

            {/* Success message banner */}
            {saveSuccessMsg && (
              <Alert severity="success" sx={{ mb: 2, fontSize: 12, fontWeight: 700 }}>
                {saveSuccessMsg}
              </Alert>
            )}

            {/* Bottom Actions: Cancel & Save Configuration */}
            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedUser(null);
                  setActiveTab("directory");
                }}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 2.5, width: { xs: "100%", sm: "auto" } }}
              >
                ✕ Cancel & Back to Directory
              </Button>
              <Button
                variant="contained"
                onClick={() => handleSaveConfig("save")}
                disabled={savingConfig}
                sx={{
                  bgcolor: "#059669",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 900,
                  px: 3,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "#047857" },
                }}
              >
                {savingConfig ? "Saving..." : "💾 Save Configuration"}
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ p: 4, borderRadius: "20px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0", textAlign: "center" }}>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#EFF6FF", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <EditRoundedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0F172A" }}>
              No Partner Selected for Configuration
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748B", mt: 0.5, maxWidth: 360, mx: "auto" }}>
              Click the <strong>Edit</strong> button on any partner in the directory to configure their Base Amount, Profit %, and activate their Terms.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setActiveTab("directory")}
              sx={{ mt: 2, borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
            >
              ← Go to Partner Directory
            </Button>
          </Paper>
        )
      )}
      </Box>
    </Box>
  );
}
